/**
 * Tasks Page
 *
 * HR and Admin view for managing tasks with weightings.
 * Shows task list with weging validation per niveau.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useTasks } from './useTasks';
import type { TaskFilters } from '../../lib/tasksApi';

export function TasksPage() {
  const [actiefFilter, setActiefFilter] = useState<boolean | undefined>();

  // Build filters object
  const filters: TaskFilters = useMemo(() => ({
    actief: actiefFilter,
  }), [actiefFilter]);

  // Fetch data
  const { data: tasksData, isLoading, error } = useTasks(filters);


  return (
    <Layout>
      <div data-testid="tasks-page">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Taken</h1>
            <p className="mt-1 text-sm text-gray-500">
              Beheer taken en wegingen per functieniveau
            </p>
          </div>
          <Link
            to="/taken/nieuw"
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nieuwe Taak
          </Link>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div>
            <label htmlFor="actief" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="actief"
              value={actiefFilter === undefined ? '' : String(actiefFilter)}
              onChange={(e) => setActiefFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
              className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Alle taken</option>
              <option value="true">Actief</option>
              <option value="false">Inactief</option>
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">
                Fout bij het laden van taken. Probeer het opnieuw.
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Laden...</span>
          </div>
        )}

        {/* Task List */}
        {!isLoading && tasksData && (
          <>
            {/* Results Count */}
            <div className="mb-4 text-sm text-gray-500">
              {tasksData.total} {tasksData.total === 1 ? 'taak' : 'taken'} gevonden
            </div>

            {/* Table */}
            {tasksData.data.length > 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Taak
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Weging
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Competenties
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Medewerkers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acties
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tasksData.data.map((task) => (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {task.name}
                            </div>
                            {task.beschrijving && (
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {task.beschrijving}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {task.weging} pt
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {task.competencies.length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {task.employeesCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            task.isActief
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {task.isActief ? 'Actief' : 'Inactief'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/taken/${task.id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Bekijken
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Geen taken gevonden
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {actiefFilter !== undefined
                    ? 'Probeer andere filteropties.'
                    : 'Begin met het toevoegen van taken.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
